import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Search, ToggleRight } from 'lucide-react';
import { CommunityGlyph } from '@/components/nav/icons';
import { openDiscordInvite } from '@/config/community';
import { useAdminRole } from '@/hooks/useAdminRole';
import { useAssessments } from '@/hooks/useAssessments';
import { useAstrologyAccess } from '@/features/astrology/hooks/useAstrologyAccess';
import { Button } from '@/components/ui/button';
import { OPTIONAL_FEATURES } from '@/features/optional/registry';
import { useOptionalFeatures } from '@/features/optional/useOptionalFeatures';
import { NAVIGATION_GROUPS, visibleNavigationItems } from '@/config/navigation';

interface CommandPaletteProps { open: boolean; onOpenChange: (open: boolean) => void; }

export const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const { isAdmin } = useAdminRole();
  const { hasTPSAssessment } = useAssessments();
  const { enabled: astrologyEnabled } = useAstrologyAccess();
  const { values } = useOptionalFeatures();
  const items = visibleNavigationItems({ isAdmin, hasTPSAssessment, astrologyEnabled }).filter((item) => !item.external);
  const handleSelect = (path: string) => { onOpenChange(false); navigate(path); };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search pages..." />
      <CommandList>
        <CommandEmpty>No pages found.</CommandEmpty>
        {NAVIGATION_GROUPS.map((group) => {
          const grouped = items.filter((item) => item.group === group);
          if (grouped.length === 0) return null;
          return (
            <CommandGroup key={group} heading={group}>
              {grouped.map((item) => (
                <CommandItem key={item.id} value={[item.label, ...(item.keywords ?? [])].join(' ')} onSelect={() => handleSelect(item.path)} className="flex cursor-pointer items-center gap-3">
                  <item.icon className="h-4 w-4 text-muted-foreground" />
                  <span>{item.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          );
        })}
        <CommandGroup heading="Optional features">
          <CommandItem onSelect={() => handleSelect('/profile?tab=account')} className="flex cursor-pointer items-center gap-3">
            <ToggleRight className="h-4 w-4 text-muted-foreground" /><span>Manage optional features</span>
          </CommandItem>
          {OPTIONAL_FEATURES.filter((feature) => feature.route && values[feature.id]).map((feature) => {
            const Icon = feature.icon;
            const route = feature.route;
            if (!route) return null;
            return <CommandItem key={feature.id} onSelect={() => handleSelect(route)} className="flex cursor-pointer items-center gap-3"><Icon className="h-4 w-4 text-muted-foreground" /><span>Open {feature.label}</span></CommandItem>;
          })}
        </CommandGroup>
        <CommandGroup heading="Community">
          <CommandItem onSelect={() => { onOpenChange(false); openDiscordInvite(); }} className="flex cursor-pointer items-center gap-3">
            <CommunityGlyph className="h-4 w-4 text-muted-foreground" /><span>Join our Discord server</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};

export const CommandPaletteTrigger: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <Button variant="ghost" size="icon" onClick={onClick} className="h-8 w-8" aria-label="Search pages (Ctrl+K)"><Search className="h-4 w-4" /></Button>
);
